import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'

import { ChildProps } from 'react-apollo'
import { InputProps } from './companyLogoMutation'

import get from 'lodash/get'
import isNil from 'lodash/isNil'

import PageLayout from 'components/layout/pageLayout'
import SubNavigation from 'components/layout/subNavigation'
import Head from 'components/layout/head'
import Button from 'components/ui/button'

interface SettingsLogoProps extends ChildProps<InputProps, Response>, RouteComponentProps<any> {
  authenticated: boolean
  companyLogoData: any
}

interface IErrorObject {
  [k: string]: string | null
}

type SettingsLogoState = {
  errors: IErrorObject | null
  success: boolean
  file: File | null
  logo: string | ArrayBuffer | null
}

class SettingsLogo extends Component<SettingsLogoProps, SettingsLogoState> {
  private fileInputRef: React.RefObject<HTMLInputElement>

  constructor(props: SettingsLogoProps) {
    super(props)
    this.fileInputRef = React.createRef()

    this.state = {
      errors: null,
      success: false,
      file: null,
      logo: null,
    }
  }

  getValue = (key: string) => {
    const {
      companyLogoData: { getCompanyLogo: companyLogoData },
    } = this.props
    const stateValue = get(this.state, key)

    if (!isNil(stateValue)) {
      return stateValue
    }

    if (key === 'logo') {
      return companyLogoData
    }

    return undefined
  }

  showSuccessMessage = () => {
    this.setState({ errors: null, success: true })

    // Toggle success message after 3 seconds
    setTimeout(() => {
      this.setState({ success: false })
    }, 3000)
  }

  showErrorMessage = () => {
    this.setState({
      errors: {
        api: 'Error Message',
      },
      success: false,
    })

    // Toggle error message after 3 seconds
    setTimeout(() => {
      this.setState({ errors: null })
    }, 3000)
  }

  handleSave = (isDeletion = false) => {
    const { file } = this.state
    const { mutate } = this.props

    if ((!isNil(file) || isDeletion) && mutate) {
      mutate({
        variables: { logo: isDeletion ? new File([''], '') : file },
        refetchQueries: ['getCompanyLogo'],
      })
        .then((res) => {
          // Company logo change was sucessfull
          if (get(res, 'data.setCompanyLogo')) {
            this.showSuccessMessage()
          } else {
            this.showErrorMessage()
          }
        })
        .catch(() => {
          this.showErrorMessage()
        })
    }
  }

  handleDelete = () => {
    this.setState({ logo: null, file: null }, () => this.handleSave(true))
  }

  handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = e

    let reader = new FileReader()
    let file = target.files ? target.files[0] : null

    reader.onloadend = () => this.setState({ file: file, logo: reader.result })

    if (file) {
      reader.readAsDataURL(file)
    }

    // Reset the file input field
    target.value = ''
  }

  // Programmatically clicks the hidden input element with type 'file'
  triggerClick = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click()
    }
  }

  render() {
    const { errors, success } = this.state

    return (
      <PageLayout pageTitle="Einstellungen">
        <Head
          headline="Einstellungen"
          subheadline={
            process.env.REACT_APP_ENV === 'rigips'
              ? 'Mengenermittlung und Systemsuche - HORST'
              : 'ABK calcKit – der Baukasten für die schnelle Kalkulation'
          }
          error={errors ? true : false}
          message={
            errors ? 'Die Datei ist zu groß!' : success ? 'Änderungen gespeichert!' : undefined
          }
          actions={
            <Button primary onClick={this.handleSave}>
              Speichern
            </Button>
          }
        />
        <SubNavigation
          items={[
            {
              label: 'Nutzerdaten',
              link: `/settings/user-data`,
            },
            {
              link: `/settings/logo`,
              label: 'Firmenlogo',
            },
            {
              link: `/settings/company-details`,
              label: 'Firmendaten',
            },
            {
              link: `/settings/reset-password`,
              label: 'Passwort ändern',
            },
            {
              link: `/settings/price-overview`,
              label: 'Preisübersicht',
            },
            {
              link: `/settings/time-overview`,
              label: 'Zeitenübersicht',
            },
          ]}
        />
        <div className="px-48">
          <p className="font-400 text-15 text-black tracking-0.1 leading-22">
            Hier können Sie ein Logo hinterlegen (Format JPG, GIF oder PNG). Dieses erscheint dann
            auf allen Drucklisten. Die optimale Abmessung für das Logo beträgt 5 cm breit und 3,5 cm
            hoch. Für eine gute Qualität empfehlen wir 300 dpi.
          </p>
          <div className="mt-45 flex justify-between">
            <div className="flex items-end">
              <div
                className="flex bg-cover bg-center bg-gray-100 flex-shrink-0 items-center justify-center"
                style={{
                  height: 130,
                  width: 130,
                  backgroundImage:
                    !isNil(this.getValue('logo')) && this.getValue('logo').length
                      ? `url(${String(this.getValue('logo'))})`
                      : ``,
                }}
              >
                {(isNil(this.getValue('logo')) || !Boolean(this.getValue('logo').length)) && (
                  <span className="font-400 text-gray-400 text-12 tracking-0.32">Logo</span>
                )}
              </div>

              <Button className="ml-30" secondary onClick={this.triggerClick}>
                Bild auswählen
              </Button>
              <input
                ref={this.fileInputRef}
                className="hidden"
                type="file"
                accept="image/png,image/jpg,image/gif"
                onChange={this.handleImageChange}
              />
            </div>

            <div className="flex items-end">
              <Button danger secondary onClick={this.handleDelete}>
                Bild löschen
              </Button>
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }
}

export default SettingsLogo
